<!-- eslint-disable camelcase -->
<template>
  <Stack>
    <p class="description">
      Your advisor has sent you one or more questionnaires. Complete them to help shape your investment experience.
    </p>
    <div class="boxes">
      <div
        v-for="questionnaire in questionnaires"
        :key="questionnaire.name"
        class="box-item"
      >
        <div class="box-head">
          <i class="box-head-icon" :class="questionnaire.icon" />
          <p class="box-head-title">
            {{ questionnaire.name }}
          </p>
        </div>
        <p class="box-description">
          {{ questionnaire.description }}
        </p>
        <Button
          :href="questionnaire.url"
          variant="primary"
          icon-after="fas fa-external-link-alt"
          target="_blank"
          resize="3_5"
        >
          {{ questionnaire.name }}
        </Button>
      </div>
    </div>
  </Stack>
</template>

<script setup>
import { computed } from 'vue';
import { useClientPortalStore } from '@frontend/ui/stores/clientPortalStore';
import { Button, Stack } from '@stakeui';

const clientPortalStore = useClientPortalStore();

const props = defineProps({
  entityExternalId: {
    type: String,
    default: '',
  },
  firmSlug: {
    type: String,
    required: false,
    default: '',
  },
  advisorSlug: {
    type: String,
    required: false,
    default: '',
  },
});

const questionnaires = computed(() => {
  const baseUrl = `${window.location.protocol}//${window.location.host}`;
  const enabledTypes = clientPortalStore.portalRequest?.questionnaire_types ?? [];

  const all = [
    {
      key: 'risk',
      name: 'Risk Questionnaire',
      icon: 'fas fa-bullseye',
      description: 'Answer a short set of questions about your financial goals, experience, and how you\'d react to real market scenarios. Your answers help your advisor build a portfolio that matches your comfort with risk.',
      url: `${baseUrl}/questionnaire/questions/v2_${btoa(props.entityExternalId)}/?questionnaire_type=risk`,
    },
    {
      key: 'values',
      name: 'Behavioral Values Questionnaire',
      icon: 'fas fa-sliders-h',
      description: 'Answer questions developed with behavioral scientists to surface what matters most to you. There are no right or wrong answers — it\'s a starting point for discussing your values with your advisor.',
      url: `${baseUrl}/questionnaire/questions/v2_${btoa(props.entityExternalId)}/`,
    },
    ...(clientPortalStore.portalContext?.advisor?.link_slug ? [{
      key: 'picker',
      name: 'Values Picker',
      icon: 'fas fa-heartbeat',
      description: 'Browse topics like climate, healthcare, and governance and select the ones important to you. Your picks help your advisor understand what you\'d like your investments to reflect.',
      url: `${baseUrl}/ui/visitor/wizard?advisor=${clientPortalStore.portalContext.advisor.link_slug}&prospect=${props.entityExternalId}`,
    }] : []),
  ];

  return all.filter(item => enabledTypes.includes(item.key));
});

</script>

<style scoped>
.description {
  margin-top: -1.25rem;
}

.boxes {
  display: grid;
  gap: 1em;
  grid-template-columns: repeat(auto-fill, minmax(18rem, 1fr));
}

.box-description {
  flex-grow: 1;
  font-size: 0.875em;
}

.box-item {
  background-color: var(--stk-scheme-main);
  border-radius: 0.75rem;
  border-style: solid;
  border-color: var(--stk-border);
  border-width: 1px;
  box-shadow: 0px 2px 2px 0px rgba(0, 0, 0, 0.02), 0px 1px 1px 0px rgba(0, 0, 0, 0.02);
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  break-inside: avoid;
}

.box-head {
  align-items: center;
  display: flex;
  gap: 0.5rem;
}

.box-head-icon {
  color: var(--stk-primary);
}

.box-head-title {
  color: var(--stk-text-10);
  font-size: 1.125em;
  font-weight: 600;
  line-height: 1.25;
}
</style>
